// Command glyphs.
//
// The bar is read under time pressure with a thumb over it, and a word is slower to read than a
// shape. Each glyph shows the MOTION the command produces, drawn against a common convention so the
// set reads as one instrument:
//
//   • the mob is the dot in the middle of every glyph;
//   • the gate is up (the paddock's gate is at the north end, and "behind" is measured against it);
//   • the dog's path is the amber stroke.
//
// Inline SVG rather than an icon font or emoji: it inherits `currentColor`, so the active and
// spent states tint for free, and there is nothing to load — a strict-CSP artifact page and a
// phone on a bad connection both get the same thing.
//
// `stroke-width` is in user units against a 24×24 viewBox, so the glyphs scale with the button.

import type { CommandType } from '../engine/commands'

/** The mob. Every glyph has one, in the same place, so the eye reads the motion around it. */
const MOB = '<circle cx="12" cy="13" r="2.2" fill="currentColor" opacity="0.55"/>'

/** Gate at the top, drawn faintly — orientation, not information. */
const GATE = '<path d="M8 2.5h8" stroke="currentColor" stroke-width="1.6" opacity="0.35"/>'

const svg = (body: string): string =>
  `<svg class="glyph" viewBox="0 0 24 24" fill="none" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${GATE}${MOB}${body}</svg>`

/** An arrowhead at (x,y), pointing along (dx,dy). Kept short so it reads at 20px. */
const head = (x: number, y: number, dx: number, dy: number): string => {
  const n = Math.hypot(dx, dy) || 1
  const ux = dx / n
  const uy = dy / n
  const s = 3.2
  // Two barbs, rotated ±35° off the reverse heading — approximated with a fixed perpendicular mix
  // so this needs no trig at call sites.
  const bx = -ux * s
  const by = -uy * s
  const px = -uy * s * 0.5
  const py = ux * s * 0.5
  return `<path d="M${x + bx + px} ${y + by + py} L${x} ${y} L${x + bx - px} ${y + by - py}" stroke="currentColor" stroke-width="2"/>`
}

const ARC = 'stroke="currentColor" stroke-width="2"'

export const COMMAND_GLYPHS: Readonly<Record<CommandType, string>> = {
  // Clockwise cast: an arc sweeping right and round, arrowhead carrying the direction.
  'come-by': svg(
    `<path d="M12 4.5 A8.5 8.5 0 0 1 19.6 16.5" ${ARC}/>${head(19.6, 16.5, 1, 2.2)}`,
  ),
  // The mirror.
  away: svg(`<path d="M12 4.5 A8.5 8.5 0 0 0 4.4 16.5" ${ARC}/>${head(4.4, 16.5, -1, 2.2)}`),
  // Drive: the dog pushes the mob at the gate, so the arrow runs up THROUGH the mob.
  'walk-on': svg(`<path d="M12 21.5 V9" ${ARC}/>${head(12, 8, 0, -1)}`),
  // Steady: slow down. A pace mark rather than a path — two bars, like a hand held palm-down.
  steady: svg(`<path d="M6 18.5 h12" ${ARC}/><path d="M7.5 21.5 h9" ${ARC} opacity="0.5"/>`),
  // Get back: away from the mob, outward. The mirror of walk on, which points in.
  'get-back': svg(`<path d="M12 15 V20" ${ARC}/>${head(12, 21, 0, 1)}`),
  // Get behind: travel round to the far side of the mob from the gate, and stop there. The stop is
  // what distinguishes it from a flank — the arc ends on a bar rather than an arrowhead.
  'get-behind': svg(
    `<path d="M19.5 8 A8.5 8.5 0 0 1 12 21" ${ARC}/><path d="M8 21.5 h8" ${ARC}/>`,
  ),
  // Look back: turn right around, away from this mob, for the one behind. The second dot is the
  // stock left behind — the only glyph with two mobs in it, which is the whole point of it.
  'look-back': svg(
    `<path d="M7.5 19 A6.5 6.5 0 1 1 14 20.5" ${ARC}/>${head(14, 20.5, 1.6, 0.5)}<circle cx="19.5" cy="20.5" r="1.8" fill="currentColor" opacity="0.55"/>`,
  ),
  // Drop: down, on the spot. An arrow into the ground rather than across it — no travel at all.
  drop: svg(`<path d="M12 17 V20" ${ARC}/>${head(12, 21, 0, 1)}<path d="M7 22.5 h10" ${ARC} opacity="0.5"/>`),
  // That'll do: off the sheep entirely — the dog leaves the picture, so the arrow points out of it.
  'thatll-do': svg(
    `<path d="M13 18 L20 22" ${ARC}/>${head(20.5, 22.3, 1.7, 1)}<path d="M4 22 h5" ${ARC} opacity="0.3"/>`,
  ),
}

/** Bark is not a handler command but shares the bar, so it needs a mark in the same language. */
export const BARK_GLYPH = svg(
  `<path d="M12 13 m-6.5 0 a6.5 6.5 0 0 1 13 0" ${ARC} opacity="0.5"/><path d="M12 13 m-9.5 0 a9.5 9.5 0 0 1 19 0" ${ARC} opacity="0.28"/>`,
)
